import React, { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MessageCircle } from 'lucide-react';

const AgentPreviewPage = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const [searchParams] = useSearchParams();

  // Get optional display names from query params
  const companyName = searchParams.get('company') || 'Our Company';
  const agentName = searchParams.get('agent') || 'AI Sales Agent';

  useEffect(() => {
    if (!orgId) return;

    // Remove existing script if present
    const existingScript = document.getElementById('hobbes-widget-script');
    if (existingScript) {
      existingScript.remove();
    }

    // Remove any existing widget root
    const widgetRoot = document.getElementById('syra-widget-root');
    if (widgetRoot) {
      widgetRoot.remove();
    }

    const script = document.createElement('script');
    script.id = 'hobbes-widget-script';
    script.async = true;

    // Use production URL or localhost based on environment
    const isLocalhost =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';
    script.src = isLocalhost
      ? 'http://localhost:3334/widget.js'
      : 'https://agent.hihobbes.com/widget.js';

    // Configuration - use orgId as client-key
    script.setAttribute('client-key', orgId);
    script.setAttribute('mode', 'sales');
    script.setAttribute('trigger-selector', '#agent-trigger');

    document.body.appendChild(script);

    return () => {
      // Cleanup on unmount
      const scriptToRemove = document.getElementById('hobbes-widget-script');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
      const widgetRootToRemove = document.getElementById('syra-widget-root');
      if (widgetRootToRemove) {
        widgetRootToRemove.remove();
      }
    };
  }, [orgId]);

  if (!orgId) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-semibold text-heading">Invalid Preview Link</h1>
          <p className="text-muted-foreground">
            This preview link is missing the organization ID.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
      <div className="max-w-2xl w-full text-center space-y-8">
        <div className="space-y-4">
          <div className="flex justify-center">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              <MessageCircle className="h-8 w-8 text-primary" />
            </div>
          </div>
          <h1 className="text-4xl font-semibold tracking-tight text-heading sm:text-5xl">
            Meet {agentName}
          </h1>
          <p className="text-lg text-muted-foreground max-w-md mx-auto">
            Your AI sales assistant from {companyName}. Click below to start a conversation.
          </p>
        </div>

        <div className="p-8 rounded-xl border card-border card-bg shadow-sm">
          <Button
            id="agent-trigger"
            size="lg"
            className="w-full text-lg h-12 font-medium"
            onClick={() => console.log('Start conversation clicked')}
          >
            Start Conversation
          </Button>
        </div>

        <p className="text-sm text-muted-foreground">
          Powered by Hobbes
        </p>
      </div>
    </div>
  );
};

export default AgentPreviewPage;
